import { useCallback, useRef } from 'react';
import { getCachedLabel, getCachedFullLabel, reverseGeocode } from '../utils/revgeo';

export const useReverseGeocode = () => {
  const inflight = useRef<Map<string, Promise<string>>>(new Map());

  const key = (lat?: number, lng?: number) => typeof lat === 'number' && typeof lng === 'number' ? `${lat.toFixed(4)},${lng.toFixed(4)}` : '';

  const getLabelSync = useCallback((lat?: number, lng?: number) => getCachedLabel(lat, lng), []);
  const getFullLabelSync = useCallback((lat?: number, lng?: number) => getCachedFullLabel(lat, lng), []);

  const getLabel = useCallback(async (lat?: number, lng?: number): Promise<string> => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return '';
    const k = key(lat, lng);
    const cached = getCachedLabel(lat, lng);
    if (cached) return cached;
    const existing = inflight.current.get(k);
    if (existing) return existing;
    const p = reverseGeocode(lat, lng).then(r => r.label).finally(() => inflight.current.delete(k));
    inflight.current.set(k, p);
    return p;
  }, []);

  const getFullLabel = useCallback(async (lat?: number, lng?: number): Promise<string> => {
    if (typeof lat !== 'number' || typeof lng !== 'number') return '';
    const k = key(lat, lng) + '|full';
    const cached = getCachedFullLabel(lat, lng);
    if (cached) return cached;
    const existing = inflight.current.get(k);
    if (existing) return existing;
    const p = reverseGeocode(lat, lng).then(r => r.fullLabel).finally(() => inflight.current.delete(k));
    inflight.current.set(k, p);
    return p;
  }, []);

  return { getLabel, getLabelSync, getFullLabel, getFullLabelSync };
};

export default useReverseGeocode;
